import { logger } from 'helpers/logger'
import { wlsly } from 'helpers/wlsly'
import { matchPath } from 'react-router-dom'

export const userRoutesAlias = {
  $user: '/@/:username',
  '$user-post': '/@/:username/posts/:postId',
  '$user-event': '/@/:username/events/:eventId',
  '$user-channel': '/@/:username/channels/:channelId',
  '$user-media': '/@/:username/media/:mediaId',
}

export const groupRoutesAlias = {
  $group: '/g/:groupname',
  '$group-post': '/g/:groupname/posts/:postId',
  '$group-event': '/g/:groupname/events/:eventId',
  '$group-forum-post': '/g/:groupname/forum/posts/:postId',
  '$group-forum-category': '/g/:groupname/forum/c/:categorySlug',
  '$group-forum-category-post': '/g/:groupname/forum/c/:categorySlug/:postId',
  '$group-forum-tag': '/g/:groupname/forum/tags/:tagName',
  '$group-bot': '/g/:groupname/bots/@/:username',
  '$group-channel': '/g/:groupname/@/:channelname',
  '$group-channel-post': '/g/:groupname/@/:channelname/posts/:postId',
  '$group-media': '/g/:groupname/media/:mediaId',
}

export const platformRoutesAlias = {
  $root: '/',
  $tag: '/tags/:tag',
}

export const createPaths = () => {
  const data = wlsly()

  if (!data) return

  const { platformData } = data

  return platformData.apps.flatMap((app) => {
    const isLocal = app.baseUrl.includes('localhost')
    const url = isLocal
      ? app.baseUrl
      : `${platformData.baseUrl}${app.baseUrl}index.html`

    return app.paths.map((path) => {
      return {
        url,
        path: path.path,
        id: app.id,
        name: app.name,
        build: app.build,
        buildDate: app.buildDate,
      }
    })
  })
}

export const routesAlias = {
  ...userRoutesAlias,
  ...groupRoutesAlias,
  ...platformRoutesAlias,
}

type RouteAlias = keyof typeof routesAlias
export const getRouteByAlias = (alias: RouteAlias) =>
  routesAlias[alias] || alias

export const mapPlatformRoutesToPaths = () => {
  const paths = createPaths()

  return paths?.map(({ path: platformPath, ...rest }) => {
    const path = replaceRouteAliases(platformPath as RouteAlias)
    return { path, ...rest }
  })
}

export const extendPath = (path: string) =>
  path.includes('/*') || path === '/' ? path : `${path}/*`

export const findAppInfoByPathname = (
  pathname: string,
  paths = createPaths(),
) =>
  paths?.find(({ path }) => {
    const route = replaceRouteAliases(path as RouteAlias)
    return matchPath(extendPath(route), pathname)
  })

export const replaceRouteAliases = (routeString: string): string => {
  const parts = routeString.split('/')
  return parts
    .map((part) => {
      if (part.startsWith('$')) {
        const aliasValue = routesAlias[part as RouteAlias]
        return aliasValue || part
      }
      return part
    })
    .join('/')
    .replace(/\/+/g, '/')
}

const logUnrecognizedRoutes = () => {
  const unrecognizedRoutes =
    createPaths()?.filter(
      ({ path }) =>
        path.startsWith('$') &&
        !path.includes('/') &&
        !routesAlias[path as keyof typeof routesAlias],
    ) || []

  if (unrecognizedRoutes.length > 0) {
    logger.warn('Unrecognized routes found:', unrecognizedRoutes)
  }
}

logUnrecognizedRoutes()
